import { AdminBreadcrumb } from "@/components/admin/AdminBreadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
	useAdminCourse,
	useCreateCourse,
	useCreateSection,
	useDeleteModule,
	useDeleteSection,
	useReorderSections,
	useUpdateCourse,
} from "@/hooks/useAdminData";
import { updateSection } from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronDown, ChevronUp, Pencil, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const courseSchema = z.object({
	id: z.string().optional(),
	title: z.string().min(1, "Title is required"),
	description: z.string(),
	language: z.string().min(1, "Language is required"),
	price_cents: z.coerce.number().min(0),
});

type CourseFormValues = z.infer<typeof courseSchema>;

export function AdminCourseEditor() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const isNew = !id || id === "new";

	const { data: course, isLoading } = useAdminCourse(id ?? "");
	const createMutation = useCreateCourse();
	const updateMutation = useUpdateCourse(id ?? "");
	const deleteModuleMutation = useDeleteModule(id ?? "");
	const deleteSectionMutation = useDeleteSection(id ?? "");
	const createSectionMutation = useCreateSection(id ?? "");
	const reorderSectionsMutation = useReorderSections(id ?? "");
	const queryClient = useQueryClient();

	const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
	const [isSavingSection, setIsSavingSection] = useState(false);
	const [editingSectionTitle, setEditingSectionTitle] = useState("");
	const [editingSectionDesc, setEditingSectionDesc] = useState("");

	const {
		register,
		handleSubmit,
		reset,
		control,
		formState: { errors, isSubmitting },
	} = useForm<CourseFormValues>({
		resolver: zodResolver(courseSchema),
		defaultValues: {
			id: "",
			title: "",
			description: "",
			language: "en",
			price_cents: 0,
		},
	});

	useEffect(() => {
		if (course) {
			reset({
				title: course.title,
				description: course.description,
				language: course.language,
				price_cents: course.price_cents,
			});
		}
	}, [course, reset]);

	async function onSubmit(values: CourseFormValues) {
		if (isNew) {
			const courseId =
				values.id ||
				`${values.title
					.toLowerCase()
					.replace(/[^a-z0-9]+/g, "-")
					.replace(/^-|-$/g, "")}-${values.language}`;
			createMutation.mutate(
				{ ...values, id: courseId, language: values.language as "en" | "pt" },
				{
					onSuccess: (created) => {
						toast.success(t("admin.courseCreated"));
						navigate(`/admin/courses/${created.id}`);
					},
					onError: () => toast.error(t("admin.saveFailed")),
				},
			);
		} else {
			updateMutation.mutate(values, {
				onSuccess: () => toast.success(t("admin.courseSaved")),
				onError: () => toast.error(t("admin.saveFailed")),
			});
		}
	}

	function handleAddSection() {
		const nextPosition = (course?.sections?.length ?? 0) + 1;
		createSectionMutation.mutate(
			{ title: `Section ${nextPosition}`, position: nextPosition },
			{
				onSuccess: () => toast.success(t("admin.sectionCreated")),
				onError: () => toast.error(t("admin.saveFailed")),
			},
		);
	}

	function startEditingSection(sectionId: string, title: string, description: string) {
		setEditingSectionId(sectionId);
		setEditingSectionTitle(title);
		setEditingSectionDesc(description);
	}

	function cancelEditingSection() {
		setEditingSectionId(null);
		setEditingSectionTitle("");
		setEditingSectionDesc("");
	}

	async function saveSection(sectionId: string) {
		setIsSavingSection(true);
		try {
			await updateSection(sectionId, {
				title: editingSectionTitle,
				description: editingSectionDesc,
			});
			toast.success(t("admin.sectionSaved"));
			cancelEditingSection();
			queryClient.invalidateQueries({ queryKey: queryKeys.admin.course(id ?? "") });
		} catch {
			toast.error(t("admin.saveFailed"));
		} finally {
			setIsSavingSection(false);
		}
	}

	function handleMoveSection(sectionId: string, direction: "up" | "down") {
		const sorted = [...(course?.sections ?? [])].sort((a, b) => a.position - b.position);
		const idx = sorted.findIndex((s) => s.id === sectionId);
		if (direction === "up" && idx <= 0) return;
		if (direction === "down" && idx >= sorted.length - 1) return;

		const newOrder = sorted.map((s) => s.id);
		const swapIdx = direction === "up" ? idx - 1 : idx + 1;
		const a = newOrder[idx];
		const b = newOrder[swapIdx];
		if (a === undefined || b === undefined) return;
		newOrder[idx] = b;
		newOrder[swapIdx] = a;

		reorderSectionsMutation.mutate(newOrder);
	}

	if (!isNew && isLoading) {
		return (
			<div className="space-y-6">
				<Skeleton className="h-6 w-64" />
				<Skeleton className="h-8 w-48" />
				<div className="space-y-4">
					{Array.from({ length: 4 }).map((_, i) => (
						<Skeleton key={`field-${i}`} className="h-10 w-full" />
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<AdminBreadcrumb
				items={[
					{ label: t("admin.dashboard"), href: "/admin" },
					{ label: t("admin.courses"), href: "/admin/courses" },
					{
						label: isNew ? t("admin.newCourse") : (course?.title ?? t("admin.edit")),
					},
				]}
			/>

			<h1 className="text-3xl font-bold text-primary">
				{isNew ? t("admin.newCourse") : t("admin.editCourse")}
			</h1>

			<form onSubmit={handleSubmit(onSubmit)} className="max-w-2xl space-y-6">
				<div className="space-y-2">
					<Label htmlFor="title">{t("admin.title")}</Label>
					<Input
						id="title"
						{...register("title")}
						placeholder={t("admin.courseTitlePlaceholder")}
					/>
					{errors.title && <p className="text-sm text-error">{errors.title.message}</p>}
				</div>

				<div className="space-y-2">
					<Label htmlFor="description">{t("admin.description")}</Label>
					<Textarea
						id="description"
						{...register("description")}
						placeholder={t("admin.descriptionPlaceholder")}
						rows={6}
					/>
				</div>

				<div className="grid grid-cols-2 gap-4">
					<div className="space-y-2">
						<Label>{t("admin.language")}</Label>
						<Controller
							control={control}
							name="language"
							render={({ field }) => (
								<Select value={field.value} onValueChange={field.onChange}>
									<SelectTrigger>
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="en">English</SelectItem>
										<SelectItem value="pt">Português</SelectItem>
									</SelectContent>
								</Select>
							)}
						/>
					</div>

					<div className="space-y-2">
						<Label htmlFor="price_cents">
							{t("admin.price")} ({t("admin.inCents")})
						</Label>
						<Input id="price_cents" type="number" {...register("price_cents")} min={0} />
					</div>
				</div>

				<div className="flex gap-3">
					<Button
						type="submit"
						disabled={isSubmitting || createMutation.isPending || updateMutation.isPending}
					>
						{isNew ? t("admin.createCourse") : t("admin.save")}
					</Button>
					<Button type="button" variant="outline" onClick={() => navigate("/admin/courses")}>
						{t("admin.cancel")}
					</Button>
				</div>
			</form>

			{/* Sections & modules — only shown when editing existing course */}
			{!isNew && course?.sections && (
				<div className="max-w-2xl space-y-6">
					{course.sections.length === 0 ? (
						<div className="rounded-xl border border-white/10 bg-bg-elevated p-8 text-center">
							<p className="text-secondary">{t("admin.noSections")}</p>
							<p className="mt-1 text-sm text-muted">{t("admin.noSectionsDesc")}</p>
						</div>
					) : (
						course.sections
							.sort((a, b) => a.position - b.position)
							.map((section, sectionIndex, sortedSections) => (
								<div key={section.id} className="space-y-2">
									<div className="flex items-center justify-between gap-2">
										{editingSectionId === section.id ? (
											<div className="flex flex-1 flex-col gap-2">
												<Input
													value={editingSectionTitle}
													onChange={(e) => setEditingSectionTitle(e.target.value)}
													placeholder={t("admin.sectionTitlePlaceholder")}
													autoFocus
												/>
												<Input
													value={editingSectionDesc}
													onChange={(e) => setEditingSectionDesc(e.target.value)}
													placeholder={t("admin.sectionDescriptionPlaceholder")}
												/>
											</div>
										) : (
											<div className="min-w-0">
												<h3 className="text-lg font-semibold text-primary">{section.title}</h3>
												{section.description && (
													<p className="text-sm text-secondary">{section.description}</p>
												)}
											</div>
										)}
										<div className="flex items-center gap-1">
											{editingSectionId === section.id ? (
												<>
													<Button
														variant="ghost"
														size="icon"
														onClick={() => saveSection(section.id)}
														disabled={isSavingSection}
													>
														<Check className="h-4 w-4" />
													</Button>
													<Button variant="ghost" size="icon" onClick={cancelEditingSection}>
														<X className="h-4 w-4" />
													</Button>
												</>
											) : (
												<>
													<Button
														variant="ghost"
														size="icon"
														onClick={() =>
															startEditingSection(section.id, section.title, section.description)
														}
													>
														<Pencil className="h-4 w-4" />
													</Button>
													{sectionIndex > 0 && (
														<Button
															variant="ghost"
															size="icon"
															onClick={() => handleMoveSection(section.id, "up")}
															disabled={reorderSectionsMutation.isPending}
														>
															<ChevronUp className="h-4 w-4" />
														</Button>
													)}
													{sectionIndex < sortedSections.length - 1 && (
														<Button
															variant="ghost"
															size="icon"
															onClick={() => handleMoveSection(section.id, "down")}
															disabled={reorderSectionsMutation.isPending}
														>
															<ChevronDown className="h-4 w-4" />
														</Button>
													)}
													<Button asChild variant="ghost" size="sm">
														<a href={`/admin/courses/${id}/modules/new?section=${section.id}`}>
															<Plus className="mr-1 h-4 w-4" />
															{t("admin.addModule")}
														</a>
													</Button>
													{section.modules.length === 0 && (
														<Button
															variant="ghost"
															size="icon"
															className="text-error hover:text-error"
															onClick={() => deleteSectionMutation.mutate(section.id)}
														>
															<Trash2 className="h-4 w-4" />
														</Button>
													)}
												</>
											)}
										</div>
									</div>
									{section.modules.length > 0 && (
										<div className="divide-y divide-white/5 rounded-xl border border-white/10 bg-bg-elevated">
											{section.modules
												.sort((a, b) => a.position - b.position)
												.map((mod) => (
													<div key={mod.id} className="flex items-center gap-3 p-4">
														<span className="flex h-7 w-7 items-center justify-center rounded-md bg-bg-overlay text-xs font-medium text-secondary">
															{mod.position}
														</span>
														<div className="flex-1 min-w-0">
															<p className="truncate font-medium text-primary">{mod.title}</p>
															<p className="text-xs text-secondary">
																{Math.floor(mod.duration_sec / 60)}m
															</p>
														</div>
														{mod.has_quiz && (
															<Badge className="border-transparent bg-info-muted text-info">
																Quiz
															</Badge>
														)}
														<div className="flex items-center gap-1">
															<Button
																variant="ghost"
																size="icon"
																onClick={() => navigate(`/admin/courses/${id}/modules/${mod.id}`)}
															>
																<Pencil className="h-4 w-4" />
															</Button>
															<Button
																variant="ghost"
																size="icon"
																className="text-error hover:text-error"
																onClick={() => deleteModuleMutation.mutate(mod.id)}
															>
																<Trash2 className="h-4 w-4" />
															</Button>
														</div>
													</div>
												))}
										</div>
									)}
								</div>
							))
					)}

					<Button
						variant="outline"
						onClick={handleAddSection}
						disabled={createSectionMutation.isPending}
					>
						<Plus className="mr-1 h-4 w-4" />
						{t("admin.addSection")}
					</Button>
				</div>
			)}
		</div>
	);
}
